export interface SSEReaderOptions {
    url: string;
    /** A getter lets reconnect attempts pick up a refreshed access token. */
    token: string | (() => string | null);
    signal: AbortSignal;
    onMessage: (data: unknown) => void;
    onConnect?: () => void;
    onDisconnect?: (err: unknown) => void;
}

const BACKOFF_STEPS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000] as const;
const STABLE_CONNECTION_MS = 5_000;

const wait = (ms: number, signal: AbortSignal): Promise<void> =>
    new Promise((resolve) => {
        if (signal.aborted) {
            resolve();
            return;
        }
        const timer = setTimeout(() => {
            signal.removeEventListener("abort", onAbort);
            resolve();
        }, ms);
        const onAbort = () => {
            clearTimeout(timer);
            signal.removeEventListener("abort", onAbort);
            resolve();
        };
        signal.addEventListener("abort", onAbort, { once: true });
    });

export async function runSSE(opts: SSEReaderOptions): Promise<void> {
    const { signal } = opts;
    let attempt = 0;

    while (!signal.aborted) {
        const connectedAt = await connectAndConsume(opts);
        if (signal.aborted) return;

        // Reset backoff if the connection was alive long enough.
        if (connectedAt !== null && Date.now() - connectedAt >= STABLE_CONNECTION_MS) {
            attempt = 0;
        }

        const delay = BACKOFF_STEPS_MS[Math.min(attempt, BACKOFF_STEPS_MS.length - 1)] ?? 30_000;
        attempt += 1;
        await wait(delay, signal);
    }
}

async function connectAndConsume(opts: SSEReaderOptions): Promise<number | null> {
    const { url, signal, onMessage, onConnect, onDisconnect } = opts;
    const token = typeof opts.token === "function" ? opts.token() : opts.token;
    if (!token) {
        onDisconnect?.(new Error("SSE access token is unavailable"));
        return null;
    }
    let res: Response;
    try {
        res = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: "text/event-stream",
            },
            signal,
        });
    } catch (err) {
        if (signal.aborted) return null;
        onDisconnect?.(err);
        return null;
    }

    if (!res.ok || !res.body) {
        onDisconnect?.(new Error(`SSE upstream returned ${res.status}`));
        return null;
    }

    const connectedAt = Date.now();
    onConnect?.();

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";

            for (const line of lines) {
                if (!line.startsWith("data: ")) continue;
                try {
                    onMessage(JSON.parse(line.slice(6)));
                } catch {
                    // heartbeat-comment or malformed payload — ignore
                }
            }
        }
    } catch (err) {
        if (signal.aborted) return connectedAt;
        onDisconnect?.(err);
        return connectedAt;
    }

    onDisconnect?.(new Error("SSE stream closed by upstream"));
    return connectedAt;
}

// Single-shot reader for callers that don't need reconnect semantics yet
// (currently `useBalanceSSE`). New callers should prefer `runSSE`, which
// adds exponential backoff and stays alive across stream drops.
export async function createSSEReader(
    url: string,
    token: string,
    signal: AbortSignal,
    onMessage: (data: unknown) => void,
): Promise<void> {
    await connectAndConsume({ url, token, signal, onMessage });
}
